import styled from 'styled-components'
import moment from 'moment'
import { Link } from 'react-router-dom'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media } from 'styles/media'

import {
  ParagraphMicro,
  ParagraphXs,
  ParagraphXxs,
} from 'components/display/Typography'
import IconChevronRight from 'components/icons/CheveronRight'
import LazyImage from 'components/display/LazyImage'

import MediaWrapper from '../shared/MediaWrapper'

const OuterLink = styled(Link)`
  &:hover {
    text-decoration: none;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px;
  gap: 8px;
  background-color: ${({ theme }) => theme?.['-ds-information-100']?.value};
  border-radius: 8px;
  margin-bottom: 20px;
  width: 100%;

  ${media.md`
    flex-direction: column;
    min-width: 240px;
  `}
`

const Image = styled.div`
  width: 40%;
  flex-shrink: 0;

  ${media.md`
    width: 100%;
  `}
`

const BookingContent = styled.div`
  flex: 1;
  align-self: center;
  padding: 8px 0;
  overflow: hidden;

  ${media.md`
    width: 100%;
  `}

  p:not(:last-child) {
    margin-bottom: 4px;
  }

  p {
    letter-spacing: normal;
  }

  ${ParagraphXs} {
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  }
`

const Annotation = styled(ParagraphMicro).attrs({ weight: '400' })`
  margin: 0;
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  `}
`

const Dates = styled(ParagraphXxs)`
  margin: 0;
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
`

const BookingArrow = styled.div`
  width: 18px;
  text-align: center;
  align-self: center;

  ${media.md`
    display: none;
  `}

  svg {
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
    width: 100%;
    max-height: 15px;
  }
`

interface IncompleteBookingCardProps {
  bookingId: string
  title: string
  dates: {
    startDate: string
    endDate: string
  }
  image: string
}

const IncompleteBookingCard = ({
  bookingId,
  title,
  dates,
  image,
}: IncompleteBookingCardProps) => {
  const { md: largerThanMd } = useMediaQueryContext()

  return (
    <OuterLink
      to={`/my/bookings/${bookingId}/payment`}
      aria-label="Complete booking"
    >
      <Wrapper>
        <Image>
          <MediaWrapper>
            <LazyImage
              data-src={image}
              alt={title}
              style={{ position: 'absolute', top: 0 }}
            />
          </MediaWrapper>
        </Image>
        <BookingContent>
          <Annotation>Complete your booking</Annotation>
          <ParagraphXs
            noMargin
            weight="400"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </ParagraphXs>
          <Dates>
            {moment(dates.startDate).format('ddd, D MMM')} -{' '}
            {moment(dates.endDate).format('ddd, D MMM')}
          </Dates>
        </BookingContent>

        {!largerThanMd && (
          <BookingArrow>
            <IconChevronRight />
          </BookingArrow>
        )}
      </Wrapper>
    </OuterLink>
  )
}

export default IncompleteBookingCard
